import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";

const rfqSchema = z.object({
  company: z.string().trim().min(1, "Company is required").max(200, "Company name too long"),
  contact_name: z.string().trim().min(1, "Contact name is required").max(100, "Name too long"),
  email: z.string().trim().email("Valid email is required").max(255, "Email too long"),
  whatsapp: z.string().trim().max(20, "Phone number too long").optional(),
  incoterm: z.enum(["FCA", "CIP", "CPT", "DAP", "DPU", "DDP"]),
  airport: z.string().trim().min(1, "Airport is required").max(10, "Airport code too long"),
  delivery_address: z.string().trim().max(500, "Address too long").optional(),
  target_date: z.string().min(1, "Target date is required"),
  products: z.string().trim().min(1, "Products are required").max(2000, "Products description too long"),
  volumes: z.string().trim().min(1, "Volumes are required").max(1000, "Volumes description too long"),
  notes: z.string().trim().max(2000, "Notes too long").optional(),
  // Honeypot field - should always be empty (bots fill this)
  fax_number: z.string().max(0, "").optional(),
}).refine((data) => {
  // Make delivery_address required for DAP, DPU and DDP incoterms
  if ((data.incoterm === "DAP" || data.incoterm === "DPU" || data.incoterm === "DDP") && !data.delivery_address) {
    return false;
  }
  return true;
}, {
  message: "Delivery address is required for DAP, DPU and DDP incoterms",
  path: ["delivery_address"],
});

type RFQFormData = z.infer<typeof rfqSchema>;

interface RFQDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RFQDrawer = ({ open, onOpenChange }: RFQDrawerProps) => {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<RFQFormData>({
    resolver: zodResolver(rfqSchema),
  });

  const selectedIncoterm = watch("incoterm");

  const onSubmit = async (data: RFQFormData) => {
    setIsSubmitting(true);
    try {
      // Submit via secure edge function (handles honeypot, rate limiting, validation server-side)
      const { data: result, error } = await supabase.functions.invoke('submit-rfq', {
        body: {
          company: data.company,
          contact_name: data.contact_name,
          email: data.email,
          whatsapp: data.whatsapp,
          incoterm: data.incoterm,
          airport: data.airport,
          delivery_address: data.delivery_address,
          target_date: data.target_date,
          products: data.products,
          volumes: data.volumes,
          notes: data.notes,
          language,
          fax_number: data.fax_number, // Honeypot - validated server-side
        }
      });

      if (error) {
        // Check for rate limit error
        if (error.message?.includes('429') || error.message?.includes('Too many')) {
          toast({
            title: "Error",
            description: "Too many submissions. Please try again later.",
            variant: "destructive",
          });
          return;
        }
        throw error;
      }

      toast({
        title: t('form.success.title'),
        description: t('form.success.message'),
      });

      reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting RFQ:', error);
      toast({
        title: "Error",
        description: t('form.error'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('form.rfq.title')}</SheetTitle>
          <SheetDescription>
            {t('form.success.message')}
          </SheetDescription>
        </SheetHeader>


        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-6 relative">
          <div className="space-y-2">
            <Label htmlFor="company">{t('form.company')}</Label>
            <Input
              id="company"
              {...register("company")}
              className={errors.company ? "border-destructive" : ""}
            />
            {errors.company && (
              <p className="text-sm text-destructive">{errors.company.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_name">{t('form.contact')}</Label>
            <Input
              id="contact_name"
              {...register("contact_name")}
              className={errors.contact_name ? "border-destructive" : ""}
            />
            {errors.contact_name && (
              <p className="text-sm text-destructive">{errors.contact_name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t('form.email')}</Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              className={errors.email ? "border-destructive" : ""}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="whatsapp">{t('form.whatsapp')}</Label>
            <Input
              id="whatsapp"
              {...register("whatsapp")}
              placeholder="+971 50 123 4567"
              className={errors.whatsapp ? "border-destructive" : ""}
            />
            {errors.whatsapp && (
              <p className="text-sm text-destructive">{errors.whatsapp.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('form.incoterm')}</Label>
              <Select onValueChange={(value) => setValue("incoterm", value as "FCA" | "CIP" | "CPT" | "DAP" | "DPU" | "DDP")}>
                <SelectTrigger>
                  <SelectValue placeholder="Select INCOTERM" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FCA">FCA</SelectItem>
                  <SelectItem value="CIP">CIP</SelectItem>
                  <SelectItem value="CPT">CPT</SelectItem>
                  <SelectItem value="DAP">DAP</SelectItem>
                  <SelectItem value="DPU">DPU</SelectItem>
                  <SelectItem value="DDP">DDP</SelectItem>
                </SelectContent>
              </Select>
              {errors.incoterm && (
                <p className="text-sm text-destructive">{errors.incoterm.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="airport">{t('form.airport')}</Label>
              <Input
                id="airport"
                {...register("airport")}
                placeholder="e.g., UIO"
                className={errors.airport ? "border-destructive" : ""}
              />
              {errors.airport && (
                <p className="text-sm text-destructive">{errors.airport.message}</p>
              )}
            </div>
          </div>

          {(selectedIncoterm === "DAP" || selectedIncoterm === "DPU" || selectedIncoterm === "DDP") && (
            <div className="space-y-2">
              <Label htmlFor="delivery_address">
                Delivery Address <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="delivery_address"
                {...register("delivery_address")}
                placeholder="Complete delivery address in Dubai..."
                className={errors.delivery_address ? "border-destructive" : ""}
              />
              {errors.delivery_address && (
                <p className="text-sm text-destructive">{errors.delivery_address.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Required for DAP, DPU and DDP deliveries
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="target_date">
              {t('form.target_date')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="target_date"
              type="date"
              {...register("target_date")}
              className={errors.target_date ? "border-destructive" : ""}
            />
            {errors.target_date && (
              <p className="text-sm text-destructive">{errors.target_date.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="products">Names/Types/Colors/Lengths</Label>
            <Textarea
              id="products"
              {...register("products")}
              placeholder="e.g., Explorer/Roses/Red/50cm"
              className={errors.products ? "border-destructive" : ""}
            />
            {errors.products && (
              <p className="text-sm text-destructive">{errors.products.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="volumes">{t('form.volumes')}</Label>
            <Textarea
              id="volumes"
              {...register("volumes")}
              placeholder="e.g., 2,500 stems weekly, 10,000 stems monthly..."
              className={errors.volumes ? "border-destructive" : ""}
            />
            {errors.volumes && (
              <p className="text-sm text-destructive">{errors.volumes.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('form.notes')}</Label>
            <Textarea
              id="notes"
              {...register("notes")}
              placeholder="Additional requirements or questions..."
            />
          </div>

          {/* Honeypot field - hidden from users, bots will fill it */}
          <div className="absolute -left-[9999px]" aria-hidden="true">
            <Label htmlFor="fax_number">Fax Number</Label>
            <Input
              id="fax_number"
              type="text"
              {...register("fax_number")}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('form.submitting') : t('form.submit')}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
};